import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation, fetchFormData } from '../../../../Api/ApiMethods';

const InterlocutoryRulingDataModal = ({ 
    apiEndpoint, 
    isOpen, 
    toggle, 
    mode, 
    selectedInterlocutoryRuling, 
    refreshData, 
    arbitrationId 
}) => {
    const [formData, setFormData] = useState({
        arbitration: [],
        arrivalDate: '',
        lastDay: '',
        primaryExpertWitness: [],
        secondaryExpertWitness: [],
        expertWitnessPrice: '',
        isDeposited: false,
        comment: '' // UI'da tek yorum alanı
    });

    const [expertWitnesses, setExpertWitnesses] = useState([]);
    const [loading, setLoading] = useState(false);

    // API endpoints for dropdowns
    const expertWitnessApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/ExpertWitness';

    useEffect(() => {
        if (isOpen) {
            // Fetch dropdown data
            fetchDropdownData();
            
            if (mode === 'modify' && selectedInterlocutoryRuling) {
                // API'den gelen comments array'inden ilk comment'i al
                const existingComment = selectedInterlocutoryRuling.comments && selectedInterlocutoryRuling.comments.length > 0 
                    ? selectedInterlocutoryRuling.comments[0].comment 
                    : '';

                setFormData({
                    ...selectedInterlocutoryRuling,
                    arrivalDate: selectedInterlocutoryRuling.arrivalDate ? 
                        new Date(selectedInterlocutoryRuling.arrivalDate).toISOString().split('T')[0] : '',
                    lastDay: selectedInterlocutoryRuling.lastDay ? 
                        new Date(selectedInterlocutoryRuling.lastDay).toISOString().split('T')[0] : '',
                    primaryExpertWitness: selectedInterlocutoryRuling.primaryExpertWitness?.id ? 
                        [selectedInterlocutoryRuling.primaryExpertWitness.id] : [],
                    secondaryExpertWitness: selectedInterlocutoryRuling.secondaryExpertWitness?.id ? 
                        [selectedInterlocutoryRuling.secondaryExpertWitness.id] : [],
                    expertWitnessPrice: selectedInterlocutoryRuling.expertWitnessPrice?.toString() || '',
                    comment: existingComment // UI'da tek yorum alanı
                });
            } else {
                setFormData({
                    arbitration: [arbitrationId],
                    arrivalDate: '',
                    lastDay: '',
                    primaryExpertWitness: [],
                    secondaryExpertWitness: [],
                    expertWitnessPrice: '',
                    isDeposited: false,
                    comment: '' // UI'da tek yorum alanı
                });
            }
        }
    }, [isOpen, mode, selectedInterlocutoryRuling, arbitrationId]);

    const fetchDropdownData = async () => {
        try {
            // Fetch expert witnesses with pagination
            const expertWitnessResponse = await fetchFormData({
                apiEndpoint: expertWitnessApiUrl,
                page: 1,
                pageSize: 1000, // Get all expert witnesses
            });
            
            // Handle different response structures
            if (expertWitnessResponse?.items) {
                setExpertWitnesses(expertWitnessResponse.items);
            } else if (expertWitnessResponse?.data?.items) {
                setExpertWitnesses(expertWitnessResponse.data.items);
            } else if (Array.isArray(expertWitnessResponse)) {
                setExpertWitnesses(expertWitnessResponse);
            } else {
                console.warn('Unexpected ExpertWitness API response structure:', expertWitnessResponse);
                setExpertWitnesses([]);
            }
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
            toast.error('Bilirkişi verileri yüklenirken hata oluştu');
            setExpertWitnesses([]);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'expertWitnessPrice') {
            // ExpenseDataModal'daki tutar alanı mantığı
            let processedValue = value.replace(/[^0-9.,]/g, '');
            processedValue = processedValue.replace(',', '.');
            const decimalCheck = processedValue.split('.');
            if (decimalCheck.length > 2) {
                processedValue = decimalCheck[0] + '.' + decimalCheck.slice(1).join('');
            }
            if (decimalCheck[1] && decimalCheck[1].length > 2) {
                processedValue = decimalCheck[0] + '.' + decimalCheck[1].slice(0, 2);
            }
            setFormData(prev => ({ ...prev, [name]: processedValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value ? [value] : [] }));
    };

    const handleMultiSelectChange = (name, selectedOptions) => {
        const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
        setFormData(prev => ({ ...prev, [name]: values }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // API'ye göndermek için comments array'i oluştur
            const submitData = {
                arbitration: formData.arbitration,
                arrivalDate: formData.arrivalDate ? new Date(formData.arrivalDate).toISOString() : null,
                lastDay: formData.lastDay ? new Date(formData.lastDay).toISOString() : null,
                primaryExpertWitness: formData.primaryExpertWitness,
                secondaryExpertWitness: formData.secondaryExpertWitness,
                expertWitnessPrice: formData.expertWitnessPrice ? Number(String(formData.expertWitnessPrice).replace(/,/g, '.')) || 0 : 0, // ExpenseDataModal mantığı
                isDeposited: formData.isDeposited,
                comments: formData.comment ? [{ // UI'daki tek yorum'u comments array'ine çevir
                    commentType: "Genel",
                    comment: formData.comment
                }] : [],
                expenses: [] // Boş array
            };

            await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? selectedInterlocutoryRuling : null,
                mode,
                data: submitData,
                refreshDataCallback: refreshData,
                componentName: 'Ara karar'
            });

            toggle();
        } catch (error) {
            console.error('Error submitting form:', error);
            // Error already handled by handleAddModifyOperation
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {mode === 'add' ? 'Ara Karar Ekle' : 'Ara Karar Düzenle'}
            </ModalHeader>
            <form onSubmit={handleSubmit}>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="arrivalDate">Geliş Tarihi</Label>
                                <Input
                                    type="date"
                                    id="arrivalDate"
                                    name="arrivalDate"
                                    value={formData.arrivalDate}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="lastDay">Son Gün</Label>
                                <Input
                                    type="date"
                                    id="lastDay"
                                    name="lastDay"
                                    value={formData.lastDay}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="primaryExpertWitness">Birinci Bilirkişi</Label>
                                <Input
                                    type="select"
                                    id="primaryExpertWitness"
                                    name="primaryExpertWitness"
                                    value={formData.primaryExpertWitness?.[0] || ''}
                                    onChange={(e) => handleSelectChange('primaryExpertWitness', e.target.value)}
                                >
                                    <option value="">Seçiniz...</option>
                                    {expertWitnesses.map(expert => (
                                        <option key={expert.id} value={expert.id}>
                                            {expert.fullName}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="secondaryExpertWitness">İkinci Bilirkişi</Label>
                                <Input
                                    type="select"
                                    id="secondaryExpertWitness"
                                    name="secondaryExpertWitness"
                                    value={formData.secondaryExpertWitness?.[0] || ''}
                                    onChange={(e) => handleSelectChange('secondaryExpertWitness', e.target.value)}
                                >
                                    <option value="">Seçiniz...</option>
                                    {expertWitnesses.map(expert => (
                                        <option key={expert.id} value={expert.id}>
                                            {expert.fullName}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="expertWitnessPrice">Bilirkişi Ücreti</Label>
                                <Input
                                    type="text"
                                    id="expertWitnessPrice"
                                    name="expertWitnessPrice"
                                    value={formData.expertWitnessPrice || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 5000.75"
                                />
                                <small className="text-muted">Ondalık ayırıcı olarak nokta (.) kullanınız</small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup className="mb-3">
                        <Row className="align-items-center">
                            <Col xl={4} md={6}>
                                <Label className="text-nowrap mb-0">Yatırıldı mı?</Label>
                            </Col>
                            <Col xl={8} md={6} className='mt-md-0 mt-2'>
                                <div className="d-flex">
                                    <div className="radio radio-primary me-3 d-flex align-items-center">
                                        <Input
                                            name="isDeposited"
                                            type="radio"
                                            value="true"
                                            id="depositedYes"
                                            checked={formData.isDeposited === true}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isDeposited: e.target.value === 'true' }))}
                                        />
                                        <Label check for="depositedYes" className="ms-1 mb-0">Evet</Label>
                                    </div>
                                    <div className="radio radio-primary d-flex align-items-center">
                                        <Input
                                            name="isDeposited"
                                            type="radio"
                                            value="false"
                                            id="depositedNo"
                                            checked={formData.isDeposited === false}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isDeposited: e.target.value === 'true' }))}
                                        />
                                        <Label check for="depositedNo" className="ms-1 mb-0">Hayır</Label>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </FormGroup>

                    <FormGroup>
                        <Label for="comment">Yorum</Label>
                        <Input
                            type="textarea"
                            rows="4"
                            id="comment"
                            name="comment"
                            value={formData.comment}
                            onChange={handleInputChange}
                            placeholder="Ara karar ile ilgili yorumunuzu yazınız..."
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>İptal</Button>
                    <Button color="primary" type="submit" disabled={loading}>
                        {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default InterlocutoryRulingDataModal;
